import React, { useEffect, useState } from 'react';
import { courseCandidatesService } from '../../../services/courseCandidatesService';
import {
  SkillFramework,
  skillFrameworkService,
} from '../../../services/skillFrameworkService';
import { SmallButton, TextArea, TextField } from './editors';

const CEFR_OPTIONS = ['A1-A2', 'A2-B1', 'B1', 'B1-B2', 'B2', 'B2-C1', 'C1-C2'];

/** Create a course: start from a skill in the framework (recommended) or from
    scratch. Defaults tuned for marketplace microlearning. */
export function NewCourseModal({ onCreated, onClose }: {
  onCreated: (courseId: string) => void;
  onClose: () => void;
}) {
  const [framework, setFramework] = useState<SkillFramework | null>(null);
  const [category, setCategory] = useState('');
  const [skill, setSkill] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [audience, setAudience] = useState('');
  const [cefr, setCefr] = useState('B1-B2');
  const [countMode, setCountMode] = useState<'approx' | 'exact'>('approx');
  const [lessonCount, setLessonCount] = useState(4);
  const [minutes, setMinutes] = useState(7);
  const [guidance, setGuidance] = useState(
    'Marketplace microlearning for OpenSesame; tight lessons, practice-heavy, with listening exercises.',
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    skillFrameworkService.get().then(setFramework).catch(() => setFramework(null));
  }, []);

  const pickSkill = (nextCategory: string, nextSkill: string) => {
    setCategory(nextCategory);
    setSkill(nextSkill);
    if (!title.trim() && nextSkill) setTitle(nextSkill);
  };

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const candidate = await courseCandidatesService.create({
        title,
        description,
        audience,
        cefr_level: cefr,
        english_variant: 'American',
        skill_focus: category && skill ? [{ category, skill }] : [],
        suggested_lesson_count: lessonCount,
        lesson_count_mode: countMode,
        approx_lesson_minutes: minutes,
        test_policy: 'none',
        additional_guidance: guidance,
      } as Parameters<typeof courseCandidatesService.create>[0]);
      onCreated(candidate.course_id);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl p-5 w-full max-w-lg space-y-3 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-semibold text-gray-900">New course</h3>
        {error && <div className="text-sm text-red-600">{error}</div>}

        <div className="p-3 bg-blue-50/60 border border-blue-100 rounded-md space-y-2">
          <div className="text-xs font-medium text-blue-900">Start from a skill</div>
          <div className="grid grid-cols-2 gap-2">
            <select value={category}
              onChange={(e) => {
                const c = e.target.value;
                const first = framework?.categories.find((x) => x.category === c)?.skills[0]?.skill ?? '';
                pickSkill(c, first);
              }}
              className="border border-gray-300 rounded-md px-2 py-1 text-sm bg-white">
              <option value="">Category…</option>
              {(framework?.categories ?? []).map((c) => <option key={c.category}>{c.category}</option>)}
            </select>
            <select value={skill} disabled={!category}
              onChange={(e) => pickSkill(category, e.target.value)}
              className="border border-gray-300 rounded-md px-2 py-1 text-sm bg-white">
              {(framework?.categories.find((c) => c.category === category)?.skills ?? [])
                .map((s) => <option key={s.skill}>{s.skill}</option>)}
            </select>
          </div>
          <div className="text-xs text-blue-800/70">
            Sets the course's skill focus (used across design and exercises). Or skip and fill
            the form from scratch.
          </div>
        </div>

        <TextField label="Title" value={title} onChange={setTitle} />
        <TextArea label="Description (what the learner will be able to do)" value={description}
          onChange={setDescription} rows={3} />
        <TextField label="Audience" value={audience} onChange={setAudience} />
        <div className="grid grid-cols-4 gap-3">
          <label className="block">
            <span className="block text-xs text-gray-500 mb-0.5">Level</span>
            <select value={cefr} onChange={(e) => setCefr(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm bg-white">
              {CEFR_OPTIONS.map((c) => <option key={c}>{c}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="block text-xs text-gray-500 mb-0.5">Lessons</span>
            <select value={countMode}
              onChange={(e) => setCountMode(e.target.value as 'approx' | 'exact')}
              className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm bg-white">
              <option value="approx">Approximately</option>
              <option value="exact">Exactly</option>
            </select>
          </label>
          <label className="block">
            <span className="block text-xs text-gray-500 mb-0.5">Count</span>
            <input type="number" min={1} max={12} value={lessonCount}
              onChange={(e) => setLessonCount(Number(e.target.value))}
              className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm" />
          </label>
          <label className="block">
            <span className="block text-xs text-gray-500 mb-0.5">Min/lesson</span>
            <input type="number" min={3} max={20} value={minutes}
              onChange={(e) => setMinutes(Number(e.target.value))}
              className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm" />
          </label>
        </div>
        <TextArea label="Guidance for the designer" value={guidance} onChange={setGuidance} rows={2} />
        <div className="flex justify-end gap-2 pt-1">
          <SmallButton onClick={onClose} disabled={busy}>Cancel</SmallButton>
          <SmallButton tone="primary" onClick={create}
            disabled={busy || !title.trim() || !audience.trim()}>
            {busy ? 'Creating…' : 'Create & start design'}
          </SmallButton>
        </div>
      </div>
    </div>
  );
}
